import { ReactElement } from 'react';

import { QueryObserverLoadingResult, QueryObserverSuccessResult } from '@tanstack/react-query';

import { QueryError, UseQueryResult } from '@arrived/queries';

export type QueryResultSuccess<T> = QueryObserverSuccessResult<T, QueryError | undefined>;

export type QueryResultSuccessOrLoading<T> =
  | QueryObserverSuccessResult<T, QueryError | undefined>
  | QueryObserverLoadingResult<T, QueryError | undefined>;

export type QueryArray<T1, T2, T3, T4, T5, T6, T7, T8> =
  | [UseQueryResult<T1>]
  | [UseQueryResult<T1>, UseQueryResult<T2>]
  | [UseQueryResult<T1>, UseQueryResult<T2>, UseQueryResult<T3>]
  | [UseQueryResult<T1>, UseQueryResult<T2>, UseQueryResult<T3>, UseQueryResult<T4>]
  | [UseQueryResult<T1>, UseQueryResult<T2>, UseQueryResult<T3>, UseQueryResult<T4>, UseQueryResult<T5>]
  | [
      UseQueryResult<T1>,
      UseQueryResult<T2>,
      UseQueryResult<T3>,
      UseQueryResult<T4>,
      UseQueryResult<T5>,
      UseQueryResult<T6>
    ]
  | [
      UseQueryResult<T1>,
      UseQueryResult<T2>,
      UseQueryResult<T3>,
      UseQueryResult<T4>,
      UseQueryResult<T5>,
      UseQueryResult<T6>,
      UseQueryResult<T7>
    ]
  | [
      UseQueryResult<T1>,
      UseQueryResult<T2>,
      UseQueryResult<T3>,
      UseQueryResult<T4>,
      UseQueryResult<T5>,
      UseQueryResult<T6>,
      UseQueryResult<T7>,
      UseQueryResult<T8>
    ];

export type QueryNotErrorArray<T1, T2, T3, T4, T5, T6, T7, T8> =
  | [QueryResultSuccessOrLoading<T1>]
  | [QueryResultSuccessOrLoading<T1>, QueryResultSuccessOrLoading<T2>]
  | [QueryResultSuccessOrLoading<T1>, QueryResultSuccessOrLoading<T2>, QueryResultSuccessOrLoading<T3>]
  | [
      QueryResultSuccessOrLoading<T1>,
      QueryResultSuccessOrLoading<T2>,
      QueryResultSuccessOrLoading<T3>,
      QueryResultSuccessOrLoading<T4>
    ]
  | [
      QueryResultSuccessOrLoading<T1>,
      QueryResultSuccessOrLoading<T2>,
      QueryResultSuccessOrLoading<T3>,
      QueryResultSuccessOrLoading<T4>,
      QueryResultSuccessOrLoading<T5>
    ]
  | [
      QueryResultSuccessOrLoading<T1>,
      QueryResultSuccessOrLoading<T2>,
      QueryResultSuccessOrLoading<T3>,
      QueryResultSuccessOrLoading<T4>,
      QueryResultSuccessOrLoading<T5>,
      QueryResultSuccessOrLoading<T6>
    ]
  | [
      QueryResultSuccessOrLoading<T1>,
      QueryResultSuccessOrLoading<T2>,
      QueryResultSuccessOrLoading<T3>,
      QueryResultSuccessOrLoading<T4>,
      QueryResultSuccessOrLoading<T5>,
      QueryResultSuccessOrLoading<T6>,
      QueryResultSuccessOrLoading<T7>
    ]
  | [
      QueryResultSuccessOrLoading<T1>,
      QueryResultSuccessOrLoading<T2>,
      QueryResultSuccessOrLoading<T3>,
      QueryResultSuccessOrLoading<T4>,
      QueryResultSuccessOrLoading<T5>,
      QueryResultSuccessOrLoading<T6>,
      QueryResultSuccessOrLoading<T7>,
      QueryResultSuccessOrLoading<T8>
    ];

export type QuerySuccessArray<T1, T2, T3, T4, T5, T6, T7, T8> = T2 extends undefined
  ? [QueryResultSuccess<T1>]
  : T3 extends undefined
  ? [QueryResultSuccess<T1>, QueryResultSuccess<T2>]
  : T4 extends undefined
  ? [QueryResultSuccess<T1>, QueryResultSuccess<T2>, QueryResultSuccess<T3>]
  : T5 extends undefined
  ? [QueryResultSuccess<T1>, QueryResultSuccess<T2>, QueryResultSuccess<T3>, QueryResultSuccess<T4>]
  : T6 extends undefined
  ? [
      QueryResultSuccess<T1>,
      QueryResultSuccess<T2>,
      QueryResultSuccess<T3>,
      QueryResultSuccess<T4>,
      QueryResultSuccess<T5>
    ]
  : T7 extends undefined
  ? [
      QueryResultSuccess<T1>,
      QueryResultSuccess<T2>,
      QueryResultSuccess<T3>,
      QueryResultSuccess<T4>,
      QueryResultSuccess<T5>,
      QueryResultSuccess<T6>
    ]
  : T8 extends undefined
  ? [
      QueryResultSuccess<T1>,
      QueryResultSuccess<T2>,
      QueryResultSuccess<T3>,
      QueryResultSuccess<T4>,
      QueryResultSuccess<T5>,
      QueryResultSuccess<T6>,
      QueryResultSuccess<T7>
    ]
  : [
      QueryResultSuccess<T1>,
      QueryResultSuccess<T2>,
      QueryResultSuccess<T3>,
      QueryResultSuccess<T4>,
      QueryResultSuccess<T5>,
      QueryResultSuccess<T6>,
      QueryResultSuccess<T7>,
      QueryResultSuccess<T8>
    ];

export interface DataStateHandlerProps<T1, T2, T3, T4, T5, T6, T7, T8> {
  children: (...args: QuerySuccessArray<T1, T2, T3, T4, T5, T6, T7, T8>) => ReactElement | null;
  queries: QueryArray<T1, T2, T3, T4, T5, T6, T7, T8>;
}

export const queriesAreNotError = <T1, T2, T3, T4, T5, T6, T7, T8>(
  queries: QueryArray<T1, T2, T3, T4, T5, T6, T7, T8>
): queries is QueryNotErrorArray<T1, T2, T3, T4, T5, T6, T7, T8> => {
  return queries.every((query) => !query.isError);
};

export const queriesAreSuccess = <T1, T2, T3, T4, T5, T6, T7, T8>(
  queries: QueryNotErrorArray<T1, T2, T3, T4, T5, T6, T7, T8>
): queries is QuerySuccessArray<T1, T2, T3, T4, T5, T6, T7, T8> => {
  return queries.every((query) => query.isSuccess);
};
